/* dd-product-card.js
 * ──────────────────
 * Click-delegation safety net for `.dd-card` instances rendered by
 * snippets/dd-product-card.liquid. The card markup uses a transparent
 * `<a class="dd-card__media-link">` overlay (position:absolute; inset:0;
 * z-index:1) sitting above the cross-fade images so the whole media
 * area links to the PDP without nesting anchors (nested <a> is invalid
 * HTML and caused the ATC pill to escape the card on the previous
 * structure). On most pages the CSS overlay alone is enough, but in
 * practice the overlay link occasionally loses the hit-test to its
 * parent `<div class="dd-card__media">` — the symptom users hit was
 * "hover image not clickable". Several stacking-context quirks can
 * cause this:
 *   • `will-change: transform` on `.dd-card__img` promotes the image
 *     to its own compositor layer, which can win the hit test in
 *     certain Chrome/Safari builds even though the image has
 *     `pointer-events: none`.
 *   • `aspect-ratio` containers + `position: absolute; inset: 0` on
 *     an empty <a> sometimes resolve to 0×0 during the brief window
 *     between layout passes and image decode, which never recovers
 *     for hit-testing in some scroll/snap cases.
 *   • Section Rendering API responses (collection filters, infinite
 *     scroll) re-insert card markup without re-running CSS, which
 *     can leave the overlay z-index detached from the new stacking
 *     context.
 *
 * Rather than fight every one of those edge cases in CSS, this script
 * adds a single document-level click delegate that catches any click
 * inside `.dd-card__media` and forwards it to the overlay's href.
 * Real interactive children (QV button, ATC pill, anything inside an
 * <a>/<button>) keep their default behaviour because we early-out
 * the moment `closest()` finds one of those above our target.
 *
 * Idempotent: `window.__ddCardClickInit` flag prevents double-binding
 * if this asset is included from multiple sections during a Section
 * Rendering API swap.
 */
(function () {
  'use strict';
  if (window.__ddCardClickInit) return;
  window.__ddCardClickInit = true;

  /**
   * Selectors that own their own click behaviour and must not be
   * intercepted. `.dd-card__qv` triggers quickview / PDP nav, the ATC
   * pill is a real anchor, and `.dd-card__title a` is the title link.
   * Any plain anchor or button anywhere in the subtree is also
   * respected — this keeps the script forward-compatible with future
   * card additions (e.g. a wishlist heart, swatch picker) without
   * having to extend the allow-list every time.
   */
  var INTERACTIVE_SELECTOR = 'a, button, [role="button"], input, select, textarea, label';

  document.addEventListener(
    'click',
    function (event) {
      // Bail on modified clicks so cmd/ctrl/shift-click keep their
      // native behaviour (open in new tab, save link, etc.).
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.defaultPrevented) return;

      var target = event.target;
      if (!target || target.nodeType !== 1) return;

      // Find the nearest media wrapper. Scoping to `.dd-card__media`
      // (rather than the outer `.dd-card`) means clicks on the title
      // and price area below the image don't get hijacked — those
      // already have their own anchors in the info block.
      var media = target.closest('.dd-card__media');
      if (!media) return;

      // If the click landed on (or inside) any real interactive
      // element, let the browser handle it. closest() walks up the
      // DOM until it hits the media wrapper, so this also covers
      // SVG children inside the QV/ATC anchors.
      var interactive = target.closest(INTERACTIVE_SELECTOR);
      if (interactive && media.contains(interactive)) return;

      // Look up the overlay link rendered by the snippet. We trust
      // its href as the source of truth so the JS path mirrors the
      // CSS path — no second source of product URLs to keep in sync.
      var link = media.querySelector('.dd-card__media-link');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href) return;

      event.preventDefault();
      window.location.href = href;
    },
    // Use bubbling phase + non-passive so we can call preventDefault
    // when forwarding the click.
    false
  );
})();
